import { OrderChargeType } from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useDeleteChargeMutation } from "Order/Modules/Payment/Api/Mutation/useDeleteChargeMutation";
import { useUpdateChargeMutation } from "Order/Modules/Payment/Api/Mutation/useUpdateChargeMutation";
import { ActionButton } from "Order/Modules/Payment/Components/ActionButton";
import {
	type EditChargeFormModel,
	getEditChargeFormModelSchema,
} from "Order/Modules/Payment/Components/Forms/Schema/editChargeFormSchema";
import type { OrderCharge } from "Order/Modules/Payment/Types/orderCharge";
import { OrderPaths } from "Order/Routes/Routing";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import { FormAutocomplete } from "Shared/Components/Form/CustomFormComponent/FormAutocomplete";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select, {
	type OptionType,
} from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Delete } from "@mui/icons-material";
import { Grid2, Link, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router";

type EditChargeFormProps = {
	charge: OrderCharge;
	orderId: number;
	currentPartnerId: number | null;
	partnerOptions: OptionType[];
};

const paymentResource = Resources.base.payment.form;
const selectOptionsResource = Resources.base.payment.selectOptions;

export function EditChargeForm({
	charge,
	orderId,
	currentPartnerId,
	partnerOptions,
}: EditChargeFormProps) {
	const isCurrentMonthCharge = dayjs().isSame(
		dayjs(charge.dateCreated),
		"month",
	);
	const { t } = useResource();
	const form = useForm<EditChargeFormModel>({
		resolver: zodResolver(getEditChargeFormModelSchema()),
		defaultValues: {
			dateCreated: charge.dateCreated,
			type: charge.type,
			amount: charge.amount,
			partnerId: charge.partnerId,
		},
	});

	const {
		control,
		formState: { errors, isDirty },
	} = form;

	const { mutate: updateCharge, isPending: isPendingUpdate } =
		useUpdateChargeMutation(charge.orderId, charge.id, orderId);

	const { mutate: deleteCharge, isPending: isPendingDelete } =
		useDeleteChargeMutation(charge.orderId, charge.id, orderId);

	useEffect(() => {
		form.reset({
			type: charge.type,
			amount: charge.amount,
			dateCreated: charge.dateCreated,
			partnerId: charge.partnerId,
		});
	}, [charge]);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError((data, options) => {
			updateCharge(
				{
					type: data.type,
					amount: data.amount,
					partnerId: data.partnerId,
				},
				options,
			);
		});
	};

	const hasChargeUpdatePermission = hasPermission(
		UserPermissions.OrderChargeModule.update,
	);

	const hasChargeDeletePermission = hasPermission(
		UserPermissions.OrderChargeModule.delete,
	);
	const isDifferentResponsiblePartner =
		charge.partnerId != null &&
		currentPartnerId != null &&
		charge.partnerId !== currentPartnerId;
	const hasPartnerSelector = partnerOptions.length > 0;

	return (
		<Stack width="100%" gap={1}>
			{charge.orderId !== orderId && (
				<Typography variant="body2" color="text.secondary">
					{t(Resources.base.payment.shared.deductedFromOrder)}{" "}
					<Link
						component={RouterLink}
						to={OrderPaths.Payment.path(charge.orderId)}
					>
						#{charge.orderId}
					</Link>
				</Typography>
			)}
			<Grid2 container mb={1} spacing={{ xs: 0, sm: 2 }}>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<DatePicker
						name="dateCreated"
						label={t(paymentResource.dateCreated)}
						control={control}
						errors={errors}
						disabled
						readOnly
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					<Select
						id="type"
						name="type"
						label={t(paymentResource.type)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasChargeUpdatePermission}
						fullWidth
						options={createSelectOptions(
							OrderChargeType,
							t,
							selectOptionsResource.chargeType,
						)}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
					<Input
						id="amount"
						name="amount"
						label={t(paymentResource.amount)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasChargeUpdatePermission}
						fullWidth
						isNumber
					/>
				</Grid2>
				{hasPartnerSelector && (
					<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
						<FormAutocomplete
							id={`partnerId-${charge.id}`}
							name="partnerId"
							label={t(paymentResource.responsiblePartner)}
							control={control}
							errors={errors}
							disabled={!hasChargeUpdatePermission}
							fullWidth
							numeric
							groupBy={(option) => option.group ?? ""}
							options={partnerOptions}
						/>
						{isDifferentResponsiblePartner && (
							<Typography variant="caption" color="warning.main">
								{t(paymentResource.differentResponsiblePartner)}
							</Typography>
						)}
					</Grid2>
				)}
				<Grid2 size={{ xs: 12, md: 1 }}>
					{!isDirty && isCurrentMonthCharge && (
						<ActionButton
							color="warning"
							disabled={!hasChargeDeletePermission}
							loading={isPendingDelete}
							onClick={deleteCharge}
						>
							<Delete />
						</ActionButton>
					)}
					{isDirty && isCurrentMonthCharge && (
						<ActionButton
							color="success"
							disabled={!hasChargeUpdatePermission}
							loading={isPendingUpdate}
							onClick={handleSubmitUpdate}
						>
							<Check />
						</ActionButton>
					)}
				</Grid2>
			</Grid2>
		</Stack>
	);
}
